import { format } from 'date-fns'
import { CornerDownRight, Image as ImageIcon, Loader2, Pencil, Send, Trash2 } from 'lucide-react'
import CommentBody from '../ui/CommentBody'
import MentionTextarea from '../ui/MentionTextarea'
import ReactionsBar from '../ui/ReactionsBar'
import {
    extractLegacyCommentImages,
    getInitials,
    isImageAttachment,
    stripLegacyImageLines,
} from './cardDetailUtils'

export default function CardCommentItem({
    t,
    comment,
    isReply = false,
    comments,
    attachments,
    commentReactions,
    currentUserId,
    editingCommentId,
    editingCommentText,
    setEditingCommentId,
    setEditingCommentText,
    updateComment,
    removeComment,
    showAchievement,
    openLightbox,
    setPreviewFile,
    handleToggleCommentReaction,
    replyingTo,
    setReplyingTo,
    replyText,
    setReplyText,
    replySubmitting,
    replyImageInputRef,
    handleCommentImageUpload,
    handleAddReply,
    companyMembers,
    uploadingFile,
}) {
    const isOwn = comment.user_id === currentUserId
    const isEditing = editingCommentId === comment.id
    const linkedImages = attachments.filter((att) => att.comment_id === comment.id && isImageAttachment(att))
    const legacyImages = extractLegacyCommentImages(comment.text)
    const commentImages = [...linkedImages]
    for (const legacy of legacyImages) {
        if (!commentImages.some((att) => att.url === legacy.url)) commentImages.push(legacy)
    }
    const displayText = stripLegacyImageLines(comment.text)
    const cReactions = commentReactions.filter((r) => r.comment_id === comment.id)
    const replies = comments.filter((x) => x.parent_comment_id === comment.id)
    const threadParentId = comment.parent_comment_id || comment.id

    return (
        <div key={comment.id} className={`comment-item ${isReply ? 'comment-item-reply' : ''}`.trim()}>
            <div
                className="comment-avatar"
                style={{ background: `hsl(${(comment.profiles?.full_name?.length || 5) * 37 % 360}, 50%, 40%)` }}
            >
                {getInitials(comment.profiles?.full_name)}
            </div>
            <div className="comment-content">
                <div className="comment-header">
                    <span className="comment-author">{comment.profiles?.full_name || t('card.comments.unknownAuthor')}</span>
                    <span className="comment-date">{format(new Date(comment.created_at), 'dd MMM, HH:mm')}</span>
                    {isOwn && !isEditing && (
                        <span className="comment-actions">
                            <button
                                type="button"
                                className="comment-action-btn"
                                title={t('card.comments.editTooltip')}
                                onClick={() => {
                                    setEditingCommentId(comment.id)
                                    setEditingCommentText(comment.text)
                                }}
                            >
                                <Pencil size={11} />
                            </button>
                            <button
                                type="button"
                                className="comment-action-btn comment-action-danger"
                                title={t('card.comments.deleteTooltip')}
                                onClick={async () => {
                                    if (!window.confirm(t('card.comments.confirmDelete'))) return
                                    const res = await removeComment(comment.id)
                                    if (!res.success) showAchievement({ type: 'error', message: res.error })
                                }}
                            >
                                <Trash2 size={11} />
                            </button>
                        </span>
                    )}
                </div>
                {isEditing ? (
                    <div className="comment-edit">
                        <textarea
                            className="comment-edit-input"
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            rows={3}
                            autoFocus
                        />
                        <div className="comment-edit-actions">
                            <button
                                type="button"
                                className="comment-edit-cancel"
                                onClick={() => { setEditingCommentId(null); setEditingCommentText('') }}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="comment-edit-save"
                                onClick={async () => {
                                    const text = editingCommentText.trim()
                                    if (!text) return
                                    const res = await updateComment(comment.id, text)
                                    if (res.success) {
                                        setEditingCommentId(null)
                                        setEditingCommentText('')
                                    } else {
                                        showAchievement({ type: 'error', message: res.error })
                                    }
                                }}
                            >
                                {t('common.save')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {displayText ? (
                            <div className="comment-body"><CommentBody text={displayText} attachments={attachments} onPreviewFile={setPreviewFile} /></div>
                        ) : null}
                        {commentImages.length > 0 ? (
                            <div className="comment-image-grid">
                                {commentImages.map((image, index) => (
                                    <button
                                        key={`${image.id || image.url}-${index}`}
                                        type="button"
                                        className="comment-image-thumb"
                                        onClick={() => openLightbox(commentImages, index)}
                                        title={image.name || 'Imagine'}
                                    >
                                        <img src={image.url} alt={image.name || 'Imagine'} />
                                    </button>
                                ))}
                            </div>
                        ) : null}
                        <div className="comment-meta-row">
                            <ReactionsBar
                                reactions={cReactions}
                                currentUserId={currentUserId}
                                onToggle={(emoji) => handleToggleCommentReaction(comment.id, emoji)}
                                size="sm"
                            />
                            <button
                                type="button"
                                className="comment-reply-btn"
                                onClick={() => { setReplyingTo(replyingTo === comment.id ? null : comment.id); setReplyText('') }}
                            >
                                <CornerDownRight size={12} /> {t('card.comments.reply', { defaultValue: 'Răspunde' })}
                            </button>
                        </div>
                        {replyingTo === comment.id && (
                            <div className="comment-reply-form">
                                <input
                                    ref={replyImageInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="sr-only"
                                    onChange={async (event) => {
                                        await handleCommentImageUpload(event.target.files, threadParentId, replyText.trim(), () => { setReplyText(''); setReplyingTo(null) })
                                        event.target.value = ''
                                    }}
                                />
                                <div className="comment-input-wrapper">
                                    <MentionTextarea
                                        value={replyText}
                                        onChange={setReplyText}
                                        onSubmit={() => handleAddReply(threadParentId)}
                                        members={companyMembers || []}
                                        files={attachments || []}
                                        placeholder={t('card.comments.replyPlaceholder', { defaultValue: 'Scrie un răspuns...' })}
                                        disabled={replySubmitting}
                                    />
                                    <button
                                        type="button"
                                        className="comment-image-button"
                                        onClick={() => replyImageInputRef.current?.click()}
                                        disabled={uploadingFile || replySubmitting}
                                        title={t('card.comments.addImage', { defaultValue: 'Adaugă poză' })}
                                    >
                                        {uploadingFile ? <Loader2 size={14} className="spin" /> : <ImageIcon size={14} />}
                                    </button>
                                    <button
                                        className="comment-send-button"
                                        onClick={() => handleAddReply(threadParentId)}
                                        disabled={!replyText.trim() || replySubmitting}
                                    >
                                        <Send size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                        {replies.length > 0 && (
                            <div className="comment-replies">
                                {replies.map((reply) => (
                                    <CardCommentItem
                                        key={reply.id}
                                        t={t}
                                        comment={reply}
                                        isReply
                                        comments={comments}
                                        attachments={attachments}
                                        commentReactions={commentReactions}
                                        currentUserId={currentUserId}
                                        editingCommentId={editingCommentId}
                                        editingCommentText={editingCommentText}
                                        setEditingCommentId={setEditingCommentId}
                                        setEditingCommentText={setEditingCommentText}
                                        updateComment={updateComment}
                                        removeComment={removeComment}
                                        showAchievement={showAchievement}
                                        openLightbox={openLightbox}
                                        setPreviewFile={setPreviewFile}
                                        handleToggleCommentReaction={handleToggleCommentReaction}
                                        replyingTo={replyingTo}
                                        setReplyingTo={setReplyingTo}
                                        replyText={replyText}
                                        setReplyText={setReplyText}
                                        replySubmitting={replySubmitting}
                                        replyImageInputRef={replyImageInputRef}
                                        handleCommentImageUpload={handleCommentImageUpload}
                                        handleAddReply={handleAddReply}
                                        companyMembers={companyMembers}
                                        uploadingFile={uploadingFile}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
